/**
 * Dialog — modal centred on desktop, full-height sheet on mobile.
 *
 * Built on @radix-ui/react-dialog so focus-trap, Esc-to-close,
 * inert-background, and screen-reader semantics are handled for us.
 *
 * Visual recipe matches the rest of the app: brand-cream surface,
 * 16px rounded corners on desktop, brand-sage hairline border, soft
 * `shadow-brand` drop. Below sm (≤640px) the panel anchors to the
 * bottom of the viewport and uses square top corners — feels like a
 * drawer without needing a separate primitive.
 *
 * Usage:
 *
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogContent>
 *       <DialogHeader>
 *         <DialogTitle>Add translation</DialogTitle>
 *         <DialogDescription>...</DialogDescription>
 *       </DialogHeader>
 *       …form…
 *       <DialogFooter>
 *         <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
 *         <Button type="submit">Save</Button>
 *       </DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '../cn.js';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-brand-ink/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
});

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hide the built-in close (X) button — set when the dialog body has
   *  its own dismiss controls. */
  hideClose?: boolean;
}

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent({ className, children, hideClose, ...props }, ref) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Mobile (default): full-width sheet anchored to bottom of
          // the viewport with square top corners — drawer-feel without
          // a separate primitive.
          'fixed inset-x-0 bottom-0 z-50 max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl border border-brand-sage/20 bg-brand-cream p-6 shadow-brand outline-none',
          // sm+: centre as a normal dialog with rounded-on-all-sides.
          'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[85vh] sm:w-[min(96vw,40rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl',
          // Radix data-state hooks for animation if tailwindcss-animate
          // is enabled (it is, via the preset).
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-top-4',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose ? (
          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-brand-ink-soft/60 transition-colors hover:bg-brand-cream-shadow hover:text-brand-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sage focus-visible:ring-offset-2"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export function DialogHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children?: ReactNode }) {
  return (
    <div className={cn('mb-5 space-y-1.5', className)} {...props}>
      {children}
    </div>
  );
}

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'text-xl font-semibold tracking-tight text-brand-ink-soft',
        className,
      )}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-brand-ink-soft/70', className)}
      {...props}
    />
  );
});

export function DialogFooter({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children?: ReactNode }) {
  return (
    <div
      className={cn(
        'mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
